import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { GLOBAL_AXIOS_TIMEOUT, nbiSource, appValues } from 'appRoot/app.constants';
import OAuth2Service from 'external-services/oauth-login/OAuth2.service';

const NS = 'NetworkRequestsService';

class NetworkRequestsService {
  private source: string;
  private globalApi: AxiosInstance;
  private globalApiWithoutToken: AxiosInstance;
  private globalApiWithoutApiHeader: AxiosInstance;
  private getToken: () => string;
  init(getToken: () => string) {
    this.getToken = getToken;
  }
  initGlobalApi() {
    const api: AxiosInstance = axios.create();
    this.getGlobalAPIHeaders(api, true);
    this.addAccessTokenInterceptor(api);
    this.addRefreshTokenInterceptor(api);
    this.globalApi = api;
  }
  getGlobalAPI() {
    if (!this.globalApi) {
      this.initGlobalApi();
    }
    return this.globalApi;
  }
  getGlobalAPIWithoutApiHeader() {
    if (!this.globalApiWithoutApiHeader) {
      const api: AxiosInstance = axios.create();
      this.getGlobalAPIHeaders(api, false);
      if (OAuth2Service.getIsEnableOAuth() && window.config?.enableOAuth2) {
        // If OAuth2 is enabled, we still need to add the interceptors for access and refresh tokens
        // even if we are not adding the API headers.
        this.addAccessTokenInterceptor(api);
        this.addRefreshTokenInterceptor(api);
      }
      this.globalApiWithoutApiHeader = api;
    }
    return this.globalApiWithoutApiHeader;
  }
  getGlobalAPIWithoutToken() {
    if (!this.globalApiWithoutToken) {
      const api: AxiosInstance = axios.create();
      this.getGlobalAPIHeaders(api, true);
      this.globalApiWithoutToken = api;
    }
    return this.globalApiWithoutToken;
  }
  // had to return 'any' type for Diary legacy compatibility, as it is on an older version of axios
  getNewAPIAxiosInstance(isAPI = false, customConfig?: AxiosRequestConfig): any {
    const api: AxiosInstance = customConfig ? axios.create(customConfig) : axios.create();
    this.getGlobalAPIHeaders(api, isAPI);
    if (isAPI) {
      this.addAccessTokenInterceptor(api);
      this.addRefreshTokenInterceptor(api);
    }
    return api;
  }
  private addAccessTokenInterceptor(api: AxiosInstance) {
    api.interceptors.request.use(
      async (config) => {
        const token = this.getToken();
        if (token) {
          config.headers['Authorization'] = 'Bearer ' + token;
        }
        return config;
      },
      function (error) {
        return Promise.reject(error);
      }
    );
  }
  // Try refresh token if 401 error
  addRefreshTokenInterceptor(api: AxiosInstance) {
    api.interceptors.response.use(
      (response) => {
        return response;
      },
      async function (error) {
        if (OAuth2Service.getIsEnableOAuth() && window.config?.enableOAuth2) {
          const originalRequest = error.config;
          if (error?.response?.status === 401 && !originalRequest._retry) {
            console.log('Intercepted 401 error, try refresh token', error);
            originalRequest._retry = true;
            const access_token = await OAuth2Service.refreshAccessToken();
            OAuth2Service.periodicalCheckForTokenValidity(access_token);
            // Preserve all original headers and only update Authorization
            originalRequest.headers = {
              ...originalRequest.headers,
              Authorization: 'Bearer ' + access_token,
            };
            // console.log('Retry request headers:', originalRequest.headers);
            return api(originalRequest);
          }
        }
        return Promise.reject(error);
      }
    );
  }
  private getGlobalAPIHeaders(api: AxiosInstance, isAPI: boolean) {
    if (isAPI) {
      (api.defaults.headers as any)['X-NBI-CorrelationId'] = this.getCorrelationId();
      (api.defaults.headers as any)['X-NBI-Source'] = this.getNBISource();
      (api.defaults.headers as any)['X-NBI-Version'] = appValues.VERSION;
    }
    api.defaults.timeout = GLOBAL_AXIOS_TIMEOUT;
  }

  getCorrelationId(): string {
    return this.uuidv4();
  }

  getNBISource(): string {
    if (!this.source) this.source = nbiSource;
    return this.source;
  }

  private uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0,
        v = c == 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}

const instance = new NetworkRequestsService();
export default instance as NetworkRequestsService;
